import { NormalizedCategory } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";

// ✅ Initial states
export const initialState: TCategoryState = {
    categories: [],
    category: null,
    size: 0
};

// ✅ Types

// Action type defn
export type CategoryAction =
    | { type: SearchActions.SET_CATEGORY_ITEMS; payload: NormalizedCategory[] }
    | { type: SearchActions.SET_ACTIVE_CATEGORY; payload: NormalizedCategory | null }
    | { type: SearchActions.SET_ACTIVE_CATEGORY_BY_KEY; payload: TSetActiveCategory }
    | { type: SearchActions.SET_IS_FETCHED; payload: TopicsFetchedParams }
    | { type: SearchActions.SET_CATEGORY_TOPIC; payload: ICategoryTopicCount }
    | { type: SearchActions.RESET_CATEGORY_TOPICS; payload: boolean };

// State type defns
export type TCategoryState = {
    /** List of normalized Categories `NormalizedCategory[]` */
    categories: NormalizedCategory[];
    /** Active/selected Category `NormalizedCategory` */
    category: NormalizedCategory | null;
    /** Number of Category items */
    size: number;
};

export interface TopicsFetchedParams {
    disclosureId: number;
    categoryId: number;
    value: boolean;
}

export interface ICategoryTopicCount {
    categoryId: number;
    disclosureId: number;
    value: number;
    isSelected: boolean;
}

export type TSetActiveCategory = {
    disclosureId: number;
    categoryProperty: TSetActiveCategoryKey;
};

export type TSetActiveCategoryKey = {
    [K in keyof NormalizedCategory]: {
        property: K;
        value: NormalizedCategory[K];
    };
}[keyof NormalizedCategory];

// Store type
export type TCategoryMethods =
    | "dispatch"
    | "getCategoriesByProperty"
    | "getCategoryByProperty"
    | "getSelectedDisclosureIds"
    | "getSelectedCategories"
    | "reset";

export type TCategoryStore = TCategoryState & {
    /**
     * Sets the Categories state using a reducer pattern.
     * @param {CategoryAction} action - `CategoryAction` action to apply.
     * @example
     * ```
     * const dispatch = useCategoryStore((state) => state.dispatch)
     * dispatch({ type: SearchActions.SET_CATEGORY_ITEMS, payload: categoryItem })
     * ```
     */
    dispatch: (action: CategoryAction) => void;
    /**
     * Retrieves one (1) or more Categories by property (key) and value using the Array `filter()` method.
     * @param property - The `NormalizedCategory` property to filter by.
     * @param value - The `property` value to match.
     * @example const filtered = getCategoriesByProperty("disclosureId", 2);
     */
    getCategoriesByProperty: <K extends keyof NormalizedCategory>(
        property: K,
        value: NormalizedCategory[K]
    ) => NormalizedCategory[] | undefined;
    /**
     * Retrieves only the 1st matching Category by property (key) and value using the Array `find()` method.
     * @param property - The `NormalizedCategory` property to filter by.
     * @param value - The `property` value to match.
     * @example const filtered = getCategoryByProperty("disclosureId", 3);
     */
    getCategoryByProperty: <K extends keyof NormalizedCategory>(
        property: K,
        value: NormalizedCategory[K]
    ) => NormalizedCategory | undefined;
    /**
     * Retrieves the Disclosure IDs of all selected Categories
     */
    getSelectedDisclosureIds: () => number[];
    /**
     * Retrieves all selected Categories whose `selectedTopicIds[]` contains one (1) or more Topic ID.
     */
    getSelectedCategories: () => NormalizedCategory[];
    /**
     * Resets the store to its initial state
     */
    reset: () => void;
};
